import React, { useMemo, memo } from 'react'
import { useCachedImage } from '../../hooks/useCachedImage'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Separator,
  Stack,
  SimpleGrid,
  Tag,
  Flex,
  Tooltip
} from '@chakra-ui/react'
import { FiCheckCircle, FiEdit2, FiShield, FiCalendar, FiStar, FiExternalLink } from 'react-icons/fi'
import { InfoTooltip } from '../InfoTooltip'

type GoogleReview = {
  author_name: string
  rating: number
  text: string
  relative_time: string
  profile_photo_url?: string
}

type ReviewsConfig = {
  google?: {
    place_id: string
    business_name: string
    connected_at: string
    last_synced_at: string
  }
  cached_reviews?: GoogleReview[]
  aggregate_rating?: number
  total_reviews?: number
  google_maps_url?: string
  display_settings?: {
    max_reviews?: number
    min_rating?: number
  }
}

type LandingPageDraft = {
  practitioner?: { name: string; specialty: string; photo_url?: string; logo_url?: string }
  cta_config?: {
    cta_type: 'book_call' | 'ask_question'
    cta_mode: 'calendar_only' | 'lead_form_then_schedule'
    book_call_destination?: 'calendar' | 'email'
    booking_url?: string
    form_fields?: string[]
    required_fields?: string[]
  }
  sections?: {
    hero?: { headline: string; subheadline: string }
    offer?: { headline: string; bullets: string[] }
    trust?: { headline: string; bullets: string[] }
    about?: { headline: string; body: string }
    faq?: { headline: string; items: Array<{ q: string; a: string }> }
    cta?: { headline: string; button_text: string; microcopy?: string }
  }
  links?: {
    website_url?: string
    social_links?: Array<{ platform: string; url: string }>
  }
}

type Props = {
  landingPage: LandingPageDraft
  reviewsConfig?: ReviewsConfig
  isEditable?: boolean
  onChange?: (next: LandingPageDraft) => void
  onOpenPanel?: (panel: 'editCopy' | 'contactOptions' | 'automation' | 'links' | 'reviews', options?: { section?: string }) => void
}

// SectionShell moved outside component to prevent recreation on every render
const SectionShell: React.FC<{
  id: string
  title?: string
  actions?: Array<{ label: string; tooltip: string; onClick: () => void }>
  children: React.ReactNode
}> = memo(({ id, title, actions, children }) => {
  return (
    <Box
      id={id}
      position="relative"
      className="lp-section"
      _hover={{
        '& .section-edit-overlay': { opacity: 1, transform: 'translateY(0px)' }
      }}
    >
      {title ? (
        <Heading size="md" color="gray.900" mb={3}>
          {title}
        </Heading>
      ) : null}

      {actions && actions.length > 0 ? (
        <Box
          position="absolute"
          top={2}
          right={2}
          opacity={0}
          transform="translateY(-2px)"
          transition="opacity 0.15s ease, transform 0.15s ease"
          className="section-edit-overlay"
          zIndex={2}
        >
          <VStack align="stretch" gap={2}>
            {actions.map((a) => (
              <Tooltip.Root key={a.label} closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Button
                    size="xs"
                    variant="solid"
                    colorPalette="brand"
                    leftIcon={<Icon as={FiEdit2} />}
                    onClick={a.onClick}
                  >
                    {a.label}
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content
                    bg="gray.900"
                    color="white"
                    px={3}
                    py={2}
                    borderRadius="md"
                    fontSize="sm"
                    maxW="320px"
                  >
                    <Tooltip.Arrow>
                      <Tooltip.ArrowTip fill="gray.900" />
                    </Tooltip.Arrow>
                    {a.tooltip}
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            ))}
          </VStack>
        </Box>
      ) : null}

      {children}
    </Box>
  )
})

// Memoized practitioner photo with blob URL caching to prevent flickering
const PractitionerPhoto = memo<{ photoUrl?: string; name?: string }>(({ photoUrl, name }) => {
  // Use cached blob URL to prevent re-fetching when signed URLs change
  const cachedPhotoUrl = useCachedImage(photoUrl)
  
  return (
    <Box
      w={{ base: '100%', md: '280px' }}
      h={{ base: '260px', md: '320px' }}
      borderRadius="2xl"
      overflow="hidden"
      bg="gray.200"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="sm"
      flexShrink={0}
    >
      {cachedPhotoUrl ? (
        <Box
          as="img"
          src={cachedPhotoUrl}
          alt={name || 'Profile photo'}
          w="100%"
          h="100%"
          objectFit="cover"
        />
      ) : (
        <Box
          w="100%"
          h="100%"
          bgGradient="linear(to-br, gray.200, gray.100)"
        />
      )}
    </Box>
  )
})

export const SpotlightLandingPagePreview: React.FC<Props> = memo(({
  landingPage,
  reviewsConfig,
  isEditable = true,
  onChange,
  onOpenPanel
}) => {
  const practitioner = landingPage.practitioner
  const sections = landingPage.sections || {}

  const ctaLabel = useMemo(() => {
    const ctaType = landingPage.cta_config?.cta_type || 'book_call'
    return ctaType === 'book_call' ? 'Book a call' : 'Ask a question'
  }, [landingPage.cta_config?.cta_type])

  const requiredFields = useMemo(() => {
    return landingPage.cta_config?.required_fields || ['name', 'email', 'phone']
  }, [landingPage.cta_config?.required_fields])

  const isCalendarMode = landingPage.cta_config?.book_call_destination === 'calendar'

  // Memoize action arrays to prevent unnecessary re-renders of SectionShell
  const heroActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit hero',
      tooltip: 'Edit the headline and subheadline people read first.',
      onClick: () => onOpenPanel('editCopy', { section: 'hero' })
    }
  ] : undefined, [onOpenPanel])

  const ctaActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit contact method',
      tooltip: 'Choose what happens when someone clicks your call-to-action button.',
      onClick: () => onOpenPanel('contactOptions', { section: 'cta' })
    }
  ] : undefined, [onOpenPanel])

  const offerActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit offer',
      tooltip: "Edit the key outcomes and what's included (bullets). Keep it scannable.",
      onClick: () => onOpenPanel('editCopy', { section: 'offer' })
    }
  ] : undefined, [onOpenPanel])

  const trustActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit testimonials',
      tooltip: 'Add testimonials, specifics, credentials, or trust signals.',
      onClick: () => onOpenPanel('editCopy', { section: 'trust' })
    }
  ] : undefined, [onOpenPanel])

  const aboutActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit about',
      tooltip: 'Explain your approach in plain language (what it feels like to work with you).',
      onClick: () => onOpenPanel('editCopy', { section: 'about' })
    }
  ] : undefined, [onOpenPanel])

  const faqActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit FAQs',
      tooltip: 'Answer the top questions people have before reaching out.',
      onClick: () => onOpenPanel('editCopy', { section: 'faq' })
    }
  ] : undefined, [onOpenPanel])

  const linksActions = useMemo(() => onOpenPanel ? [
    {
      label: 'Edit links',
      tooltip: 'Optional: add your website/social links as extra trust — not the main CTA.',
      onClick: () => onOpenPanel('links', { section: 'links' })
    }
  ] : undefined, [onOpenPanel])

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="sm"
    >
      {/* HERO (above-the-fold conversion layout) */}
      <Box
        bgGradient="linear(to-b, brand.50, white)"
        px={{ base: 5, md: 10 }}
        py={{ base: 8, md: 12 }}
      >
        <SectionShell
          id="hero"
          actions={heroActions}
        >
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 8, md: 12 }} alignItems="start">
              {/* Left: Message */}
            <VStack align="stretch" gap={5}>
              {/* Big practitioner image + identity */}
              <VStack align="start" gap={3}>
                <PractitionerPhoto 
                  photoUrl={practitioner?.photo_url} 
                  name={practitioner?.name} 
                />
                <VStack align="start" gap={0}>
                  <Text fontSize="lg" color="gray.900" fontWeight="semibold">
                    {practitioner?.name || ''}
                  </Text>
                  <Text fontSize="sm" color="brand.700" fontWeight="medium">
                    {practitioner?.specialty || ''}
                  </Text>
                </VStack>
              </VStack>

              <VStack align="stretch" gap={3}>
                <Heading size={{ base: 'xl', md: '3xl' }} color="gray.900" lineHeight="short">
                  {sections.hero?.headline || ''}
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700" lineHeight="tall">
                  {sections.hero?.subheadline || ''}
                </Text>
              </VStack>

              {/* Benefit bullets (tight, scannable) */}
              <VStack align="stretch" gap={2}>
                {(sections.offer?.bullets || []).slice(0, 3).map((b, idx) => (
                  <HStack key={idx} align="start" gap={2}>
                    <Icon as={FiCheckCircle} color="brand.600" mt={0.5} />
                    <Text fontSize="sm" color="gray.700">
                      {b}
                    </Text>
                  </HStack>
                ))}
              </VStack>

              {/* Trust microcopy chips */}
              <Flex wrap="wrap" gap={2}>
                <Tag.Root size="sm" variant="subtle" colorPalette="gray">
                  <Tag.Label>Fast reply</Tag.Label>
                </Tag.Root>
                <Tag.Root size="sm" variant="subtle" colorPalette="gray">
                  <Tag.Label>No spam</Tag.Label>
                </Tag.Root>
                <Tag.Root size="sm" variant="subtle" colorPalette="gray">
                  <Tag.Label>Plain-language guidance</Tag.Label>
                </Tag.Root>
              </Flex>
            </VStack>

            {/* Right: CTA card (what users do above the fold) */}
            <SectionShell
              id="ctaAboveFold"
              actions={ctaActions}
            >
              <Box
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="2xl"
                boxShadow="md"
                p={{ base: 5, md: 6 }}
              >
                <VStack align="stretch" gap={4}>
                  <VStack align="stretch" gap={1}>
                    <Heading size="md" color="gray.900">
                      {sections.cta?.headline || ctaLabel}
                    </Heading>
                    {sections.cta?.microcopy ? (
                      <Text fontSize="sm" color="gray.600">
                        {sections.cta.microcopy}
                      </Text>
                    ) : null}
                  </VStack>

                  <Separator />

                  {/* Show calendar placeholder or contact form based on mode */}
                  {isCalendarMode ? (
                    <VStack align="stretch" gap={4}>
                      <Box 
                        h="280px" 
                        bg="gray.50" 
                        borderRadius="md" 
                        borderWidth="1px" 
                        borderColor="gray.200"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <VStack gap={3}>
                          <Icon as={FiCalendar} boxSize={10} color="gray.400" />
                          <Text fontSize="sm" color="gray.500" textAlign="center">
                            Your Calendly scheduler<br />will appear here
                          </Text>
                          {landingPage.cta_config?.booking_url && (
                            <Text fontSize="xs" color="brand.600" fontWeight="medium">
                              Connected
                            </Text>
                          )}
                        </VStack>
                      </Box>

                      <HStack gap={2} align="start">
                        <Icon as={FiShield} color="gray.500" mt={0.5} />
                        <Text fontSize="xs" color="gray.600">
                          Book directly from this page. No account needed.
                        </Text>
                      </HStack>
                    </VStack>
                  ) : (
                    <>
                      {/* Form mock that visually matches a real landing form */}
                      <VStack align="stretch" gap={3}>
                        {(['name', 'email', 'phone'] as const).map((f) => {
                          const required = requiredFields.includes(f)
                          const label = f === 'name' ? 'Name' : f === 'email' ? 'Email' : 'Phone'
                          return (
                            <Box key={f}>
                              <HStack justify="space-between" mb={1}>
                                <Text fontSize="xs" color="gray.600">
                                  {label}
                                </Text>
                                <Text fontSize="xs" color={required ? 'gray.700' : 'gray.500'}>
                                  {required ? 'Required' : 'Optional'}
                                </Text>
                              </HStack>
                              <Box
                                h={10}
                                bg="gray.50"
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor="gray.200"
                              />
                            </Box>
                          )
                        })}
                        <Box>
                          <HStack justify="space-between" mb={1}>
                            <Text fontSize="xs" color="gray.600">
                              Message
                            </Text>
                            <Text fontSize="xs" color={requiredFields.includes('message') ? 'gray.700' : 'gray.500'}>
                              {requiredFields.includes('message') ? 'Required' : 'Optional'}
                            </Text>
                          </HStack>
                          <Box
                            h={24}
                            bg="gray.50"
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor="gray.200"
                          />
                        </Box>
                      </VStack>

                      <Tooltip.Root closeDelay={300} openDelay={200}>
                        <Tooltip.Trigger asChild>
                          <Button 
                            colorPalette="brand" 
                            size="lg" 
                            w="full"
                            onClick={() => {
                              if (onOpenPanel) {
                                onOpenPanel('contactOptions', { section: 'cta' })
                              }
                            }}
                          >
                            {sections.cta?.button_text || (landingPage.cta_config?.cta_type === 'book_call' ? 'Book a call' : 'Send')}
                          </Button>
                        </Tooltip.Trigger>
                        <Tooltip.Positioner>
                          <Tooltip.Content
                            bg="gray.900"
                            color="white"
                            px={3}
                            py={2}
                            borderRadius="md"
                            fontSize="sm"
                            maxW="320px"
                          >
                            <Tooltip.Arrow>
                              <Tooltip.ArrowTip fill="gray.900" />
                            </Tooltip.Arrow>
                            Click to configure what happens when visitors click this button
                          </Tooltip.Content>
                        </Tooltip.Positioner>
                      </Tooltip.Root>

                      <HStack gap={2} align="start">
                        <Icon as={FiShield} color="gray.500" mt={0.5} />
                        <Text fontSize="xs" color="gray.600">
                          Your details stay private. You'll get a quick response with next steps.
                        </Text>
                      </HStack>
                    </>
                  )}
                </VStack>
              </Box>
            </SectionShell>
          </SimpleGrid>
        </SectionShell>
      </Box>

      <VStack gap={10} align="stretch" px={{ base: 5, md: 8 }} py={{ base: 8, md: 10 }}>
        {/* OFFER */}
        <SectionShell
          id="offer"
          title={sections.offer?.headline || 'Offer'}
          actions={offerActions}
        >
          <VStack align="stretch" gap={2}>
            {(sections.offer?.bullets || []).map((b, idx) => (
              <Text key={idx} fontSize="md" color="gray.700">
                - {b}
              </Text>
            ))}
          </VStack>
        </SectionShell>

        {/* TRUST / REVIEWS */}
        {reviewsConfig?.google?.place_id && reviewsConfig.cached_reviews && reviewsConfig.cached_reviews.length > 0 ? (
          // Google Reviews connected - show verified reviews
          <SectionShell
            id="trust"
            title="What Clients Say"
            actions={onOpenPanel ? [
              {
                label: 'Manage reviews',
                tooltip: 'Connect or disconnect Google Reviews',
                onClick: () => onOpenPanel('reviews', { section: 'trust' })
              }
            ] : undefined}
          >
            <VStack align="stretch" gap={4}>
              {/* Google aggregate rating */}
              <HStack gap={3} pb={2}>
                <Box
                  bg="white"
                  borderRadius="md"
                  px={3}
                  py={2}
                  borderWidth="1px"
                  borderColor="gray.200"
                  boxShadow="sm"
                >
                  <HStack gap={2}>
                    <Box
                      as="img"
                      src="https://www.google.com/favicon.ico"
                      alt="Google"
                      w={4}
                      h={4}
                    />
                    <HStack gap={1}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Icon
                          key={star}
                          as={FiStar}
                          boxSize={3}
                          color={star <= Math.round(reviewsConfig.aggregate_rating || 0) ? 'yellow.400' : 'gray.300'}
                          fill={star <= Math.round(reviewsConfig.aggregate_rating || 0) ? 'yellow.400' : 'none'}
                        />
                      ))}
                    </HStack>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      {reviewsConfig.aggregate_rating?.toFixed(1)}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      ({reviewsConfig.total_reviews} reviews)
                    </Text>
                  </HStack>
                </Box>
              </HStack>

              {/* Individual reviews */}
              {reviewsConfig.cached_reviews.slice(0, 3).map((review, idx) => (
                <Box
                  key={idx}
                  bg="gray.50"
                  borderRadius="lg"
                  p={4}
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <HStack justify="space-between" mb={2}>
                    <HStack gap={2}>
                      {review.profile_photo_url ? (
                        <Box
                          as="img"
                          src={review.profile_photo_url}
                          alt={review.author_name}
                          w={8}
                          h={8}
                          borderRadius="full"
                          objectFit="cover"
                        />
                      ) : (
                        <Box
                          w={8}
                          h={8}
                          borderRadius="full"
                          bg="brand.100"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="sm" fontWeight="semibold" color="brand.700">
                            {review.author_name.charAt(0).toUpperCase()}
                          </Text>
                        </Box>
                      )}
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="medium" color="gray.900">
                          {review.author_name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {review.relative_time}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack gap={0.5}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Icon
                          key={star}
                          as={FiStar}
                          boxSize={3}
                          color={star <= review.rating ? 'yellow.400' : 'gray.300'}
                          fill={star <= review.rating ? 'yellow.400' : 'none'}
                        />
                      ))}
                    </HStack>
                  </HStack>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    "{review.text}"
                  </Text>
                </Box>
              ))}

              {/* Link to Google */}
              {reviewsConfig.google_maps_url && (
                <HStack gap={1} pt={1}>
                  <Icon as={FiExternalLink} boxSize={3} color="gray.500" />
                  <Text fontSize="xs" color="gray.500">
                    Reviews verified on Google
                  </Text>
                </HStack>
              )}
            </VStack>
          </SectionShell>
        ) : (
          // Fallback to manual testimonials
          <SectionShell
            id="trust"
            title={sections.trust?.headline || 'What Clients Say'}
            actions={onOpenPanel ? [
              {
                label: 'Connect Google Reviews',
                tooltip: 'Import verified reviews from your Google Business profile',
                onClick: () => onOpenPanel('reviews', { section: 'trust' })
              },
              ...(trustActions || [])
            ] : undefined}
          >
            {(sections.trust?.bullets || []).length > 0 ? (
              <VStack align="stretch" gap={2}>
                {(sections.trust?.bullets || []).map((b, idx) => (
                  <Box key={idx} bg="gray.50" borderRadius="md" p={4} borderWidth="1px" borderColor="gray.200">
                    <Text fontSize="sm" color="gray.700">
                      "{b}"
                    </Text>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box bg="gray.50" borderRadius="md" p={6} borderWidth="1px" borderColor="gray.200" borderStyle="dashed" textAlign="center">
                <Text fontSize="sm" color="gray.500">
                  Connect your Google Business to display verified reviews
                </Text>
              </Box>
            )}
          </SectionShell>
        )}

        {/* ABOUT */}
        <SectionShell
          id="about"
          title={sections.about?.headline || 'About'}
          actions={aboutActions}
        >
          <Text fontSize="md" color="gray.700" whiteSpace="pre-wrap" lineHeight="tall">
            {sections.about?.body || ''}
          </Text>
        </SectionShell>

        {/* FAQ */}
        <SectionShell
          id="faq"
          title={sections.faq?.headline || 'FAQ'}
          actions={faqActions}
        >
          <VStack align="stretch" gap={4}>
            {(sections.faq?.items || []).map((item, idx) => (
              <Box key={idx} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
                <Text fontSize="sm" color="gray.900" fontWeight="semibold">
                  {item.q}
                </Text>
                <Text fontSize="sm" color="gray.700" mt={2} whiteSpace="pre-wrap" lineHeight="tall">
                  {item.a}
                </Text>
              </Box>
            ))}
          </VStack>
        </SectionShell>

        {/* Links section */}
        {(landingPage.links?.website_url || (landingPage.links?.social_links || []).length > 0) && (
          <SectionShell
            id="links"
            title="Links"
            actions={linksActions}
          >
            <VStack align="stretch" gap={2}>
              {landingPage.links?.website_url ? (
                <Text fontSize="sm" color="gray.700">
                  Website: {landingPage.links.website_url}
                </Text>
              ) : null}
              {(landingPage.links?.social_links || []).map((l, idx) => (
                <Text key={idx} fontSize="sm" color="gray.700">
                  {(l.platform || 'Social').trim()}: {l.url}
                </Text>
              ))}
              <Text fontSize="xs" color="gray.500">
                Optional trust links — they should never replace the primary CTA.
              </Text>
            </VStack>
          </SectionShell>
        )}
      </VStack>
    </Box>
  )
})



